import { useTranslation } from "react-i18next";
import { CustomModal } from "../../../components/CustomModal/CustomModal";
import { CustomTextInput } from "../../../components/CustomTextInput/CustomTextInput";
import { CustomDropZoneUpload } from "../../../components/CustomDropZoneUpload/CustomDropZoneUpload";
import useStyles from "./styles";
import { FC, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useCreateSign, useUpdateSign } from "../../../api/signs";
import { uploadToStorage } from "../../../api/media";
import { Tabs, Tab, Box, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { CustomAddressInput } from "../../../components/CustomAddressInput/CustomAddressInput";
import { CustomWorkingDaysInput } from "../../../components/CustomWorkingDaysInput/CustomWorkingDaysInput";
import { CitySelect, CountrySelect } from "../../../components/CustomSelectOptions/CustomSelectOptions";

// Interface for tab panel
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sign-tabpanel-${index}`}
      aria-labelledby={`sign-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

type ModalAddSignProps = {
  open: boolean;
  signInitial: TSign;
  etablissements: TEtablissement[]; // Add etablissements prop
  onCancel?: () => void;
  onClose?: () => void;
};

export const ModalAddSign: FC<ModalAddSignProps> = ({
  open,
  signInitial,
  etablissements,
  onCancel,
  onClose,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [sign, setSign] = useState<TSign>({...signInitial});
  const [tabValue, setTabValue] = useState(0);
  /** Per-field in-flight upload flags, keyed by "logo" / "picture_1" / ... */
  const [uploadingFields, setUploadingFields] = useState<Record<string, boolean>>({});
  
  
  const resetForm = () => setSign({ 
    id: -1, 
    name: "",
    logo: null,
    phone: null,
    email: null,
    centre_code: null,
    postal_code: null,
    google_place_id: null,
    picture_1: null,
    picture_2: null,
    picture_3: null,
    etablissement_id: null, // Add etablissement_id
    etablissement: null, // Add etablissement
    address: {
      raw: "",
      lat: 0,
      lng: 0,
      city: null,
      country: null
    },
    working_day: null,
    unworking_day: null,
    services: [],
    distance: 0,
    like: false,
    created_at: null,
    updated_at: ""
  });
  
  const { mutate: createSign, isPending } = useCreateSign(
    onClose,
    resetForm
  );

  const { mutate: updateSign, isPending: loadingUpdate } = useUpdateSign(
    signInitial.id ?? -1,
    onClose,
    resetForm
  );

  useEffect(() => {
    if (signInitial?.id !== undefined && signInitial.id !== -1) {
      // Make sure the incoming sign conforms to our expected structure
      const normalizedSign: TSign = {
        ...signInitial,
        address: signInitial.address || {
          raw: "",
          lat: 0,
          lng: 0,
          city: null,
          country: null
        },
        etablissement_id: signInitial.etablissement_id || null,
        etablissement: signInitial.etablissement || null
      };
      setSign(normalizedSign);
    } else {
      resetForm();
    }
  }, [signInitial]);

  const handleInputChange = (fieldName: string, value: string | number | null) => {
    setSign(prevSign => ({
      ...prevSign,
      [fieldName]: value
    }));
  };

  const handleEtablissementChange = (etablissementId: number | null) => {
    const selectedEtablissement = etablissements.find(etab => etab.id === etablissementId);
    setSign(prevSign => ({
      ...prevSign,
      etablissement_id: etablissementId,
      etablissement: selectedEtablissement || null
    }));
  };
  
  const handleAddressChange = (address: Partial<TAddress>) => {
    setSign(prevSign => ({
      ...prevSign,
      address: {
        ...prevSign.address,
        ...address
      }
    }));
  };
  
  const handleCityChange = (cityData: { id: string | number; libelle: string }) => {
    setSign(prevSign => ({
      ...prevSign,
      address: {
        ...prevSign.address,
        city: cityData
      }
    }));
  };

  const handleCountryChange = (countryData: { id: string | number; libelle: string }) => {
    setSign(prevSign => ({
      ...prevSign,
      address: {
        ...prevSign.address,
        country: countryData
      }
    }));
  };
  
  const handleWorkingDaysChange = (value: WorkingDay[] | UnworkingDay[]) => {
    // Type guard to check if it's a WorkingDay array
    if (value.length > 0 && 'day' in value[0]) {
      setSign(prevSign => ({
        ...prevSign,
        working_day: value as WorkingDay[]
      }));
    }
  };
  
  const handleUnworkingDaysChange = (value: WorkingDay[] | UnworkingDay[]) => {
    // Type guard to check if it's an UnworkingDay array
    if (value.length > 0 && !('day' in value[0])) {
      setSign(prevSign => ({
        ...prevSign,
        unworking_day: value as UnworkingDay[]
      }));
    }
  };
  
  const handleImageUpload = (fieldName: string, fileUrl: string) => {
    setSign(prevSign => ({
      ...prevSign,
      [fieldName]: fileUrl
    }));
  };

  /**
   * BUGFIX: every image field used to be set to `URL.createObjectURL(file)`.
   * A `blob:` URL is a handle into THIS tab's memory — it was then submitted to
   * the API and written to the database, so the logo and gallery pictures were
   * dead links the moment the page reloaded, and unusable from any other
   * browser or from the mobile apps. Upload to object storage and persist the
   * real URL instead.
   */
  const handleImageDrop = async (fieldName: string, acceptedFiles: File[]) => {
    const file = acceptedFiles?.[0];
    if (!file) return;

    setUploadingFields(prev => ({ ...prev, [fieldName]: true }));
    try {
      // "public/sign": stored on the sign record as a permanent URL, so it has
      // to sit in the anonymously-readable prefix of the bucket.
      const { url } = await uploadToStorage(file, { directory: "public/sign" });
      if (!url) throw new Error("no url returned");
      handleImageUpload(fieldName, url);
    } catch (e: any) {
      console.error(`[sign] ${fieldName} upload failed:`, e);
      toast.error(
        e?.message || t("sign.imageUploadError", "Failed to upload image")
      );
    } finally {
      setUploadingFields(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const isUploadingAnyImage = Object.values(uploadingFields).some(Boolean);
  
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSave = () => {
    if (!sign?.name || sign.name.trim().length === 0) {
      toast.error(t("required.name", "Name is required"));
      return;
    }
    
    if (isUploadingAnyImage) {
      toast.error(t("sign.imagesStillUploading", "Image upload still in progress"));
      return;
    }

    const signData: RequestSign = { 
      name: sign.name,
      logo: sign.logo,
      phone: sign.phone,
      email: sign.email,
      centre_code: sign.centre_code,
      postal_code: sign.postal_code,
      google_place_id: sign.google_place_id,
      picture_1: sign.picture_1,
      picture_2: sign.picture_2,
      picture_3: sign.picture_3,
      etablissement_id: sign.etablissement_id, // Add etablissement_id to request
      address: {
        raw: sign.address.raw,
        lat: sign.address.lat,
        lng: sign.address.lng,
        city: sign.address.city?.libelle ? { libelle: sign.address.city.libelle } : undefined,
        country: sign.address.country?.libelle ? { libelle: sign.address.country.libelle } : undefined
      },
      working_day: sign.working_day || undefined,
      unworking_day: sign.unworking_day || undefined,
      services: sign.services?.map(service => service.id) || []
    };
    
    if (signInitial.id !== -1) {
      updateSign(signData);
    } else {
      createSign(signData);
    }
  };

  return (
    <CustomModal
      open={open}
      onCancel={onCancel}
      title={
        signInitial.id !== -1
          ? t("sign.modifySign", "Modify sign")
          : t("sign.addSign", "Add sign")
      }
      onClose={onClose}
      onSave={handleSave}
      loadingSave={isPending || loadingUpdate || isUploadingAnyImage}
      width="800px"
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="sign tabs">
          <Tab label={t("sign.basicInfo", "Basic Info")} />
          <Tab label={t("sign.location", "Location")} />
          <Tab label={t("sign.images", "Images")} />
          <Tab label={t("sign.hoursServices", "Hours")} />
        </Tabs>
      </Box>
      
      <TabPanel value={tabValue} index={0}>
        <div className={classes.containerForm}>
          <CustomTextInput
            label={t("common.name", "Name")}
            placeholder={t("common.name", "Name")}
            marginBottom={"15px"}
            value={sign?.name || ""}
            onChange={(e) => handleInputChange("name", e.target.value)}
          />

          {/* Etablissement Selection */}
          <FormControl fullWidth margin="normal" sx={{ marginBottom: "15px" }}>
            <InputLabel id="etablissement-select-label">
              {t("sign.etablissement", "Etablissement")}
            </InputLabel>
            <Select
              labelId="etablissement-select-label"
              id="etablissement-select"
              value={sign?.etablissement_id || ""}
              label={t("sign.etablissement", "Etablissement")}
              onChange={(e) => handleEtablissementChange(e.target.value as number || null)}
            >
              <MenuItem value="">
                <em>{t("common.none", "None")}</em>
              </MenuItem>
              {etablissements.map((etablissement) => (
                <MenuItem key={etablissement.id} value={etablissement.id}>
                  {etablissement.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <CustomTextInput
            label={t("common.phone", "Phone")}
            placeholder={t("common.phone", "Phone")}
            marginBottom={"15px"}
            value={sign?.phone || ""}
            onChange={(e) => handleInputChange("phone", e.target.value)}
          />
          
          <CustomTextInput
            label={t("common.email", "Email")}
            placeholder={t("common.email", "Email")}
            marginBottom={"15px"}
            value={sign?.email || ""}
            onChange={(e) => handleInputChange("email", e.target.value)}
          />
          
          <CustomTextInput
            label={t("sign.centreCode", "Centre Code")}
            placeholder={t("sign.centreCode", "Centre Code")}
            marginBottom={"15px"}
            value={sign?.centre_code || ""}
            onChange={(e) => handleInputChange("centre_code", e.target.value)}
          />
          
          <CustomTextInput
            label={t("common.postalCode", "Postal Code")}
            placeholder={t("common.postalCode", "Postal Code")}
            marginBottom={"0px"}
            value={sign?.postal_code || ""}
            onChange={(e) => handleInputChange("postal_code", e.target.value)}
          />
        </div>
      </TabPanel>
      
      <TabPanel value={tabValue} index={1}>
        <div className={classes.containerForm}>
          <CustomAddressInput
            label={t("common.address", "Address")}
            placeholder={t("common.enterAddress", "Enter address")}
            marginBottom={"15px"}
            value={sign?.address?.raw || ""}
            lat={sign?.address?.lat || 0}
            lng={sign?.address?.lng || 0}
            onChange={handleAddressChange}
            onGooglePlaceIdChange={(id) => handleInputChange("google_place_id", id)}
          />
          
          {/* Pass initial city data to prevent wrong display */}
          <CitySelect
            label={t("common.city", "City")}
            placeholder={t("common.selectCity", "Select a city")}
            marginBottom={"15px"}
            marginTop={"15px"}
            value={sign?.address?.city?.id ? String(sign.address.city.id) : ""}
            onChange={handleCityChange}
            initialCityData={sign?.address?.city ? {
              id: sign.address.city.id,
              libelle: sign.address.city.libelle
            } : null}
            size="medium"
          />

          {/* Pass initial country data to prevent wrong display */}
          <CountrySelect
            label={t("common.country", "Country")}
            placeholder={t("common.selectCountry", "Select a country")}
            marginBottom={"0px"}
            marginTop={"15px"}
            value={sign?.address?.country?.id ? String(sign.address.country.id) : ""}
            onChange={handleCountryChange}
            initialCountryData={sign?.address?.country ? {
              id: sign.address.country.id,
              libelle: sign.address.country.libelle
            } : null}
            size="medium"
          />
        </div>
      </TabPanel>
      
      <TabPanel value={tabValue} index={2}>
        <div className={classes.imagesContainer}>
          {/* Logo Section */}
          <div className={classes.imageSection}>
            <div className={classes.imageSectionHeader}>
              <h3 className={classes.sectionTitle}>{t("sign.logo", "Logo")}</h3>
              <span className={classes.sectionDescription}>
                {t("sign.logoDescription", "Upload your business logo (recommended: square format)")}
              </span>
            </div>
            <div className={classes.logoUploadContainer}>
              <CustomDropZoneUpload
                imageUrl={sign?.logo || ""}
                onDrop={(acceptedFiles) => handleImageDrop("logo", acceptedFiles)}
              />
            </div>
          </div>

          {/* Pictures Gallery Section */}
          <div className={classes.imageSection}>
            <div className={classes.imageSectionHeader}>
              <h3 className={classes.sectionTitle}>{t("sign.gallery", "Gallery")}</h3>
              <span className={classes.sectionDescription}>
                {t("sign.galleryDescription", "Add up to 3 photos showcasing your business")}
              </span>
            </div>
            
            <div className={classes.galleryGrid}>
              <div className={classes.galleryItem}>
                <div className={classes.galleryItemHeader}>
                  <span className={classes.galleryItemTitle}>
                    {t("sign.picture", "Picture")} 1
                  </span>
                  {sign?.picture_1 && (
                    <button
                      className={classes.removeImageBtn}
                      onClick={() => handleImageUpload("picture_1", "")}
                      title={t("common.remove", "Remove")}
                    >
                      ×
                    </button>
                  )}
                </div>
                <CustomDropZoneUpload
                  imageUrl={sign?.picture_1 || ""}
                  onDrop={(acceptedFiles) => handleImageDrop("picture_1", acceptedFiles)}
                  height="200px"
                />
              </div>

              <div className={classes.galleryItem}>
                <div className={classes.galleryItemHeader}>
                  <span className={classes.galleryItemTitle}>
                    {t("sign.picture", "Picture")} 2
                  </span>
                  {sign?.picture_2 && (
                    <button
                      className={classes.removeImageBtn}
                      onClick={() => handleImageUpload("picture_2", "")}
                      title={t("common.remove", "Remove")}
                    >
                      ×
                    </button>
                  )}
                </div>
                <CustomDropZoneUpload
                  imageUrl={sign?.picture_2 || ""}
                  onDrop={(acceptedFiles) => handleImageDrop("picture_2", acceptedFiles)}
                  height="200px"
                />
              </div>

              <div className={classes.galleryItem}>
                <div className={classes.galleryItemHeader}>
                  <span className={classes.galleryItemTitle}>
                    {t("sign.picture", "Picture")} 3
                  </span>
                  {sign?.picture_3 && (
                    <button
                      className={classes.removeImageBtn}
                      onClick={() => handleImageUpload("picture_3", "")}
                      title={t("common.remove", "Remove")}
                    >
                      ×
                    </button>
                  )}
                </div>
                <CustomDropZoneUpload
                  imageUrl={sign?.picture_3 || ""}
                  onDrop={(acceptedFiles) => handleImageDrop("picture_3", acceptedFiles)}
                  height="200px"
                />
              </div>
            </div>
          </div>
        </div>
      </TabPanel>
          
      <TabPanel value={tabValue} index={3}>
        <div className={classes.containerForm}>
          <CustomWorkingDaysInput
            label={t("sign.workingDays", "Working Days")}
            value={sign?.working_day || []}
            onChange={handleWorkingDaysChange}
            marginBottom={"15px"}
          />
          
          <CustomWorkingDaysInput
            label={t("sign.unworkingDays", "Closed Days")}
            value={sign?.unworking_day || []}
            onChange={handleUnworkingDaysChange}
            isUnworkingDays
            marginBottom={"15px"}
          />
        </div>
      </TabPanel>
    </CustomModal>
  );
};